import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_INTERVAL_MS = 8000;
// Round 218A Phase 8D-3C (2026-02-06) — once both intelligence
// types reach live_ready OR both hit terminal retry, we slow the
// poll down so we're not hammering the server forever.
const IDLE_INTERVAL_MS = 60000;

const TERMINAL_LIVE = new Set(['live_ready']);
const ACTIVE_STATES = new Set([
  'live_upgrade_queued',
  'live_upgrade_running',
  'ready_fallback',
]);

/**
 * useIntelligenceState — polls GET /api/onboarding/intelligence/state so
 * the Dashboard can auto-refresh Market Intelligence + Opportunity
 * Intelligence cards as the background scheduler upgrades them from
 * ``ready_fallback`` → ``live_ready``.
 *
 * Behavior:
 *   - Polls every 8s while any intel type is still upgrading.
 *   - Slows to 60s once both are live_ready or terminally failed.
 *   - Pauses entirely when the browser tab is hidden (Page Visibility
 *     API) — resumes immediately on visibilitychange.
 *   - Fires ``onLiveReady(type, row)`` callbacks the moment either type
 *     transitions to live_ready so cards can refresh themselves.
 *   - Auto-cancels on unmount.
 */
export default function useIntelligenceState({
  intervalMs = DEFAULT_INTERVAL_MS,
  onLiveReady,
  enabled = true,
} = {}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const prevStatesRef = useRef({ market_intelligence: null, opportunity_intelligence: null });
  const onLiveReadyRef = useRef(onLiveReady);
  onLiveReadyRef.current = onLiveReady;

  const authHeaders = useCallback(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/intelligence/state`, {
        headers: authHeaders(),
      });
      if (cancelledRef.current) return null;
      setState(res.data);
      setError(null);

      // Fire onLiveReady exactly once per transition.
      const intel = res.data?.intel || {};
      ['market_intelligence', 'opportunity_intelligence'].forEach((t) => {
        const nowState = intel[t]?.state;
        const prev = prevStatesRef.current[t];
        if (nowState === 'live_ready' && prev && prev !== 'live_ready') {
          try { onLiveReadyRef.current?.(t, intel[t]); } catch (_e) { /* ignore */ }
        }
        prevStatesRef.current[t] = nowState || null;
      });

      return res.data;
    } catch (err) {
      if (cancelledRef.current) return null;
      setError(err?.response?.data?.detail || err.message || 'Failed to load intelligence state');
      return null;
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [authHeaders]);

  const computeNextDelay = useCallback((data) => {
    const intel = data?.intel || {};
    const mi = intel.market_intelligence?.state;
    const oi = intel.opportunity_intelligence?.state;
    const bothTerminal =
      (TERMINAL_LIVE.has(mi) || mi === 'live_upgrade_failed_retry_pending') &&
      (TERMINAL_LIVE.has(oi) || oi === 'live_upgrade_failed_retry_pending');
    const anyActive =
      ACTIVE_STATES.has(mi) || ACTIVE_STATES.has(oi) ||
      mi === 'live_upgrade_running' || oi === 'live_upgrade_running';
    if (anyActive && !TERMINAL_LIVE.has(mi) === false && !TERMINAL_LIVE.has(oi) === false) {
      // both live → idle
      return IDLE_INTERVAL_MS;
    }
    if (bothTerminal) return IDLE_INTERVAL_MS;
    return intervalMs;
  }, [intervalMs]);

  useEffect(() => {
    if (!enabled) return undefined;
    cancelledRef.current = false;

    const scheduleNext = (data) => {
      if (cancelledRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return; // paused
      const delay = computeNextDelay(data);
      timerRef.current = window.setTimeout(async () => {
        const next = await fetchOnce();
        scheduleNext(next);
      }, delay);
    };

    // First fetch on mount, then poll.
    (async () => {
      const data = await fetchOnce();
      scheduleNext(data);
    })();

    const onVisibility = () => {
      if (cancelledRef.current) return;
      if (document.hidden) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      } else if (!timerRef.current) {
        // Immediately refetch on return so the customer sees the
        // latest state without waiting for the next tick.
        (async () => {
          const data = await fetchOnce();
          scheduleNext(data);
        })();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);

  const refresh = useCallback(async () => {
    setLoading(true);
    return fetchOnce();
  }, [fetchOnce]);

  return {
    state,
    loading,
    error,
    refresh,
    marketIntel: state?.intel?.market_intelligence || null,
    opportunityIntel: state?.intel?.opportunity_intelligence || null,
    onboardingVersion: state?.onboarding_version || null,
  };
}
